

import { Injectable } from '@angular/core';
import { UrlService } from '../../../../environments/urlService';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { IS_SPINNER_ACTIVE } from '../../../util/interceptors';
import {
  MaByIndustryDTO, MaByVintageDTO,
  MarketAnalysisByVintageChartDTO,
} from '../dto/MaByIndustryChartDTO';
import { map, Observable } from 'rxjs';
import { MaRecoverySetUpDTO, MaRecoveryDTO, MaReportedPeriod } from '../../recovery/component/recovery-market-analysis/pojo/ma-classes';
import { LocalDate } from '../../../core/pojo/date-variable';

@Injectable({
  providedIn: 'root',
})
export class MarketAnalysisService {

  private readonly url: string;
  private readonly recoveryUrl: string;

  constructor(
    private http: HttpClient,
    private urlService: UrlService,
  ) {
    this.url = this.urlService.getFmsUrl() + 'market-analysis/';
    this.recoveryUrl = this.urlService.getFmsUrl() + 'recovery/ma/';
  }


  getMaPeriods(): Observable<MaReportedPeriod[]> {
    return this.http.get<MaReportedPeriod[]>(this.url + 'periods/', {
      withCredentials: true,
    }).pipe(
      map((periods) => periods.map((period) => ({
        ...period,
        iconNameAndStyling1: period.isReported ? 'reported.svg' : '',
      }))),
    );
  }

  getListOfReportedPeriods(): Observable<LocalDate[]> {
    return this.getMaPeriods().pipe(
      map((periods) => periods
        .filter((p) => p.isReported)
        .map((p) => p.period),
      ),
    );
  }

  getMarketAnalysisByIndustryData(period: string): Observable<MaByIndustryDTO> {
    return this.http.get<MaByIndustryDTO>(`${this.url}by-industry/${period}`, { withCredentials: true });
  }

  getRecoveryMarketAnalysisByIndustryData(wrapper: MaRecoverySetUpDTO): Observable<MaByIndustryDTO> {
    const url = `${(this.recoveryUrl)}by-industry/chart-data`;
    return this.http.post<MaByIndustryDTO>(url, wrapper, { withCredentials: true });
  }

  getMarketAnalysisByVintageChartData(period: LocalDate, fundId: number): Observable<MarketAnalysisByVintageChartDTO> {
    return this.http.get<MarketAnalysisByVintageChartDTO>(
      this.url + 'by-vintage/chart-data/' + period + '/' + fundId,
      { withCredentials: true },
    );
  }

  calculateMaVintageData(wrapper: MaRecoverySetUpDTO): Observable<MaByVintageDTO> {
    const url = `${(this.recoveryUrl)}by-vintage/chart-data`;
    return this.http.post<MaByVintageDTO>(url, wrapper,
      { withCredentials: true });
  }

  setReportedFundPerformances(wrapper: MaRecoverySetUpDTO): Observable<void> {
    return this.http.post<void>(this.recoveryUrl + 'fp/', wrapper, {
      withCredentials: true,
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
    });
  }

  deleteReportedFundPerformances(period: LocalDate): Observable<boolean> {
    return this.http.delete<boolean>(this.recoveryUrl + period, {
      context: new HttpContext().set(IS_SPINNER_ACTIVE, true),
      withCredentials: true,
    });
  }

  getMaTables(period: LocalDate, isReported: boolean): Observable<MaRecoveryDTO> {
    const params = new HttpParams().set('isReported', isReported.toString());
    return this.http.get<MaRecoveryDTO>(this.recoveryUrl + 'funds/table/' + period, {
      withCredentials: true,
      params,
    });
  }

  recalculateMaTables(wrapper: MaRecoverySetUpDTO): Observable<MaRecoveryDTO> {
    return this.http.post<MaRecoveryDTO>(this.recoveryUrl + 'recalculate-tables', wrapper, {
      withCredentials: true,
    });
  }

  copySettingsFromPeriod(period: LocalDate, copyPeriod: LocalDate): Observable<MaRecoveryDTO> {
    return this.http.get<MaRecoveryDTO>(this.recoveryUrl + 'copy-settings-for/' + period + '/from/' + copyPeriod, {
      withCredentials: true,
    });
  }
}
